/**
 * CEO/Executive Dashboard
 * High-level analytics, real-time map, and business health metrics
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  Clock,
  Users,
  Truck,
  Store,
  MapPin,
  AlertTriangle,
  AlertCircle,
  Info,
  RefreshCw,
  Calendar,
  Download,
  Activity,
  Zap,
  Target,
  Gift,
  ChevronRight,
  Loader2,
  Shield,
  Award,
  Headphones,
  Brain,
  Route,
  Search,
} from 'lucide-react';
import VisitCounterWidget from '../components/VisitCounterWidget';
import SupportWidget from '../components/SupportWidget';
import VideoStorageWidget from "../components/VideoStorageWidget";
import ShareTrackingWidget from "../components/ShareTrackingWidget";

const COLORS = {
  green: '#00A86B',
  yellow: '#FFD300',
  black: '#000000',
  white: '#FFFFFF',
};

// Mini Sparkline Chart
const Sparkline = ({ data = [], color = COLORS.green }) => {
  const max = Math.max(...data, 1);
  const points = data.map((v, i) => ({
    x: (i / (data.length - 1)) * 100,
    y: 100 - (v / max) * 80
  }));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
  
  return (
    <svg viewBox="0 0 100 40" className="w-full h-8" preserveAspectRatio="none">
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
};

// Bar Chart Component
const BarChart = ({ data = [], height = 120 }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  
  return (
    <div className="flex items-end gap-1" style={{ height }}>
      {data.map((item, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center">
          <div
            className="w-full rounded-t transition-all duration-300 hover:opacity-80"
            style={{
              height: `${(item.value / max) * 100}%`,
              minHeight: item.value > 0 ? '4px' : '0',
              background: item.color || COLORS.green
            }}
          />
          <span className="text-[9px] text-gray-400 mt-1 truncate w-full text-center">{item.label}</span>
        </div>
      ))}
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, change, icon: Icon, sparkData = [], color = COLORS.green, prefix = '', suffix = '' }) => {
  const isPositive = change >= 0;
  
  return (
    <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700 hover:shadow-xl hover:border-gray-600 transition-all">
      <div className="flex items-start justify-between mb-2">
        <div className="p-2 rounded-xl" style={{ background: `${color}25` }}>
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
        {change !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-medium ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
            {isPositive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
            {Math.abs(change).toFixed(1)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-white mb-1">
        {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
      </p>
      <p className="text-xs text-gray-400 mb-2">{title}</p>
      {sparkData.length > 0 && <Sparkline data={sparkData} color={color} />}
    </div>
  );
};

// Alert Item Component
const AlertItem = ({ alert }) => {
  const typeStyles = {
    critical: { bg: 'bg-red-900/30', border: 'border-red-700', icon: AlertTriangle, color: 'text-red-400' },
    warning: { bg: 'bg-amber-900/30', border: 'border-amber-700', icon: AlertCircle, color: 'text-amber-400' },
    info: { bg: 'bg-blue-900/30', border: 'border-blue-700', icon: Info, color: 'text-blue-400' }
  };
  
  const style = typeStyles[alert.type] || typeStyles.info;
  const Icon = style.icon;
  
  return (
    <div className={`p-3 rounded-xl ${style.bg} ${style.border} border flex items-start gap-3`}>
      <Icon className={`h-5 w-5 ${style.color} flex-shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm ${style.color}`}>{alert.title}</p>
        <p className="text-xs text-gray-400">{alert.message}</p>
      </div>
      {alert.count && (
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${style.color} ${style.bg}`}>
          {alert.count}
        </span>
      )}
    </div>
  );
};

// Simple Map Component (2D representation)
const DeliveryMap = ({ drivers = [], deliveries = [], pudos = [] }) => {
  // Jamaica bounds
  const bounds = { minLat: 17.7, maxLat: 18.5, minLng: -78.4, maxLng: -76.2 };
  
  const toPosition = (lat, lng) => ({
    left: `${((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * 100}%`,
    top: `${100 - ((lat - bounds.minLat) / (bounds.maxLat - bounds.minLat)) * 100}%`
  });
  
  return (
    <div className="relative w-full h-64 bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl overflow-hidden border border-gray-700">
      {/* Grid lines */}
      <div className="absolute inset-0 opacity-20">
        {[...Array(5)].map((_, i) => (
          <div key={`h${i}`} className="absolute w-full border-t border-gray-600" style={{ top: `${i * 25}%` }} />
        ))}
        {[...Array(5)].map((_, i) => (
          <div key={`v${i}`} className="absolute h-full border-l border-gray-600" style={{ left: `${i * 25}%` }} />
        ))}
      </div>
      
      {/* Jamaica label */}
      <div className="absolute top-2 left-2 px-2 py-1 bg-gray-900/80 rounded text-xs font-medium text-gray-300">
        Jamaica
      </div>
      
      {/* PUDO locations */}
      {pudos.map((pudo, idx) => {
        const pos = toPosition(pudo.lat, pudo.lng);
        return (
          <div
            key={`pudo-${idx}`}
            className="absolute w-3 h-3 bg-black rounded-sm transform -translate-x-1/2 -translate-y-1/2 shadow-sm"
            style={pos}
            title={pudo.business_name}
          />
        );
      })}
      
      {/* Deliveries (lines) */}
      {deliveries.slice(0, 10).map((del, idx) => {
        if (!del.pickup_location?.lat || !del.dropoff_location?.lat) return null;
        const from = toPosition(del.pickup_location.lat, del.pickup_location.lng);
        const to = toPosition(del.dropoff_location.lat, del.dropoff_location.lng);
        return (
          <svg key={`del-${idx}`} className="absolute inset-0 w-full h-full pointer-events-none">
            <line
              x1={from.left}
              y1={from.top}
              x2={to.left}
              y2={to.top}
              stroke={COLORS.yellow}
              strokeWidth="2"
              strokeDasharray="4"
              className="animate-pulse"
            />
          </svg>
        );
      })}
      
      {/* Drivers */}
      {drivers.map((driver, idx) => {
        if (!driver.current_location?.lat) return null;
        const pos = toPosition(driver.current_location.lat, driver.current_location.lng);
        return (
          <div
            key={`driver-${idx}`}
            className="absolute w-4 h-4 bg-[#00A86B] rounded-full transform -translate-x-1/2 -translate-y-1/2 shadow-lg animate-pulse"
            style={pos}
            title={`Driver ${driver.id?.slice(0, 8)}`}
          >
            <div className="absolute inset-0 bg-[#00A86B] rounded-full animate-ping opacity-50" />
          </div>
        );
      })}
      
      {/* Legend */}
      <div className="absolute bottom-2 right-2 bg-gray-800/90 rounded-lg p-2 text-[10px] space-y-1">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-[#00A86B] rounded-full" />
          <span>Drivers ({drivers.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-black rounded-sm" />
          <span>PUDO ({pudos.length})</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 border-t-2 border-dashed border-[#FFD300]" />
          <span>Routes ({deliveries.length})</span>
        </div>
      </div>
      
      {/* Live indicator */}
      <div className="absolute top-2 right-2 flex items-center gap-1 bg-gray-800/90 rounded-full px-2 py-1">
        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
        <span className="text-[10px] font-medium">LIVE</span>
      </div>
    </div>
  );
};

// Main CEO Dashboard Component
const CEODashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [summary, setSummary] = useState(null);
  const [liveData, setLiveData] = useState({ drivers: [], deliveries: [], pudos: [] });
  const [financial, setFinancial] = useState(null);
  const [deliveryMetrics, setDeliveryMetrics] = useState(null);
  const [chainEfficiency, setChainEfficiency] = useState(null);
  const [merchantStats, setMerchantStats] = useState(null);
  const [driverStats, setDriverStats] = useState(null);
  const [incentives, setIncentives] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [searchInsights, setSearchInsights] = useState(null);
  const [productStats, setProductStats] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const fetchData = useCallback(async () => {
    try {
      const [
        summaryRes,
        driversRes,
        deliveriesRes,
        pudosRes,
        financialRes,
        payoutsRes,
        metricsRes,
        chainRes,
        merchantsRes,
        driversStatsRes,
        incentivesRes,
        alertsRes,
        productStatsRes
      ] = await Promise.all([
        api.get(`/ceo/summary?period=${period}`),
        api.get('/ceo/live/drivers'),
        api.get('/ceo/live/deliveries'),
        api.get('/ceo/live/pudos'),
        api.get(`/ceo/financial/revenue?period=${period}`),
        api.get('/ceo/financial/payouts'),
        api.get(`/ceo/delivery/metrics?period=${period}`),
        api.get('/ceo/delivery/chain-efficiency'),
        api.get('/ceo/merchants/stats'),
        api.get('/ceo/drivers/stats'),
        api.get('/ceo/incentives/impact'),
        api.get('/ceo/alerts/live'),
        api.get('/ceo/products/stats')
      ]);
      
      setSummary(summaryRes.data);
      setLiveData({
        drivers: driversRes.data.drivers || [],
        deliveries: deliveriesRes.data.deliveries || [],
        pudos: pudosRes.data.pudos || []
      });
      setFinancial({ ...financialRes.data, payouts: payoutsRes.data });
      setDeliveryMetrics(metricsRes.data);
      setChainEfficiency(chainRes.data);
      setMerchantStats(merchantsRes.data);
      setDriverStats(driversStatsRes.data);
      setIncentives(incentivesRes.data);
      setAlerts(alertsRes.data.alerts || []);
      if (productStatsRes) setProductStats(productStatsRes.data);
      
      // Fetch search analytics
      try {
        const searchRes = await api.get('/search-analytics/insights/summary?period=month');
        setSearchInsights(searchRes.data);
      } catch (e) {
        console.log('Search analytics not available');
      }
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to load CEO data');
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchData();
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const formatCurrency = (val) => `J$${(val || 0).toLocaleString()}`;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#00A86B] mx-auto mb-4" />
          <p className="text-gray-500">Loading Executive Dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pb-8">
      {/* Top Bar */}
      <header className="bg-gray-800 shadow-lg sticky top-0 z-10 border-b border-gray-700">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="p-2 hover:bg-gray-700 rounded-full"
            >
              <ArrowLeft className="h-5 w-5 text-gray-300" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-white">Executive Dashboard</h1>
              <p className="text-xs text-gray-400">Welcome, {user?.name || 'CEO'}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {/* Period Selector */}
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="text-sm border border-gray-600 rounded-lg px-3 py-1.5 bg-gray-700 text-white"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
            
            {/* Refresh */}
            <Button
              onClick={fetchData}
              variant="outline"
              size="sm"
              className="rounded-lg border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            
            {/* Live Indicator */}
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-900/50 rounded-full border border-green-700">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs font-medium text-green-400">LIVE</span>
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4 max-w-7xl mx-auto">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <VisitCounterWidget />
          <MetricCard
            title="Total GMV"
            value={summary?.gmv || 0}
            change={summary?.gmv_change}
            icon={DollarSign}
            prefix="J$"
            color={COLORS.green}
            sparkData={[3200, 4100, 3800, 5200, 4800, 6000, summary?.gmv || 0]}
          />
          <MetricCard
            title="Platform Revenue"
            value={summary?.platform_revenue || 0}
            icon={TrendingUp}
            prefix="J$"
            color={COLORS.yellow}
          />
          <MetricCard
            title="Completed Deliveries"
            value={summary?.completed_deliveries || 0}
            icon={Package}
            color={COLORS.green}
          />
          <MetricCard
            title="Avg Delivery Time"
            value={summary?.avg_delivery_time || 0}
            icon={Clock}
            suffix=" min"
            color={COLORS.black}
          />
          <MetricCard
            title="Active Drivers"
            value={`${summary?.active_drivers || 0}/${summary?.total_drivers || 0}`}
            icon={Users}
            color={COLORS.green}
          />
          <MetricCard
            title="Orders in Transit"
            value={summary?.active_orders || 0}
            icon={Truck}
            color={COLORS.yellow}
          />
        </div>

        {/* Alerts Panel (if any) */}
        {alerts.length > 0 && (
          <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Alerts & Incidents
            </h3>
            <div className="space-y-2">
              {alerts.slice(0, 4).map((alert, idx) => (
                <AlertItem key={idx} alert={alert} />
              ))}
            </div>
          </div>
        )}

        {/* Real-Time Map */}
        <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-white flex items-center gap-2">
              <MapPin className="h-5 w-5 text-[#00A86B]" />
              Real-Time Delivery Map
            </h3>
            <span className="text-xs text-gray-400">
              Updated: {lastUpdate.toLocaleTimeString()}
            </span>
          </div>
          <DeliveryMap
            drivers={liveData.drivers}
            deliveries={liveData.deliveries}
            pudos={liveData.pudos}
          />
        </div>

        {/* Financial + Delivery Row */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Financial Analytics */}
          <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-[#00A86B]" />
              Revenue Analytics
            </h3>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-green-50 rounded-xl">
                <p className="text-lg font-bold text-[#00A86B]">{formatCurrency(financial?.breakdown?.platform_fee?.total)}</p>
                <p className="text-xs text-gray-500">Platform Fees</p>
              </div>
              <div className="p-3 bg-gray-700 rounded-xl">
                <p className="text-lg font-bold text-white">{formatCurrency(financial?.payouts?.pending_driver_payouts)}</p>
                <p className="text-xs text-gray-500">Pending Driver Payouts</p>
              </div>
              <div className="p-3 bg-gray-700 rounded-xl">
                <p className="text-lg font-bold text-white">{formatCurrency(financial?.payouts?.pending_merchant_payouts)}</p>
                <p className="text-xs text-gray-500">Pending Merchant Payouts</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-xl">
                <p className="text-lg font-bold text-[#FFD300]">{financial?.seller_fee_rate}%</p>
                <p className="text-xs text-gray-500">Seller Fee Rate</p>
              </div>
            </div>
            
            {/* Daily Revenue Chart */}
            {financial?.daily_revenue?.length > 0 && (
              <div>
                <p className="text-xs text-gray-500 mb-2">Daily Revenue</p>
                <BarChart
                  data={financial.daily_revenue.slice(-7).map(d => ({
                    label: d.date.slice(5),
                    value: d.revenue,
                    color: COLORS.green
                  }))}
                  height={80}
                />
              </div>
            )}
          </div>

          {/* Delivery Operations */}
          <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
              <Activity className="h-5 w-5 text-[#00A86B]" />
              Delivery Operations
            </h3>
            
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-gray-700 rounded-xl">
                <p className="text-lg font-bold text-white">{deliveryMetrics?.total_packages || 0}</p>
                <p className="text-xs text-gray-500">Total Packages</p>
              </div>
              <div className="p-3 bg-green-50 rounded-xl">
                <p className="text-lg font-bold text-[#00A86B]">{deliveryMetrics?.delivery_rate || 0}%</p>
                <p className="text-xs text-gray-500">Delivery Rate</p>
              </div>
              <div className="p-3 bg-gray-700 rounded-xl">
                <p className="text-lg font-bold text-white">{chainEfficiency?.avg_legs_per_delivery || 1}</p>
                <p className="text-xs text-gray-500">Avg Chain Legs</p>
              </div>
              <div className="p-3 bg-gray-700 rounded-xl">
                <p className="text-lg font-bold text-white">{chainEfficiency?.pudo_transitions || 0}</p>
                <p className="text-xs text-gray-500">PUDO Transitions</p>
              </div>
            </div>
            
            {/* Status Breakdown */}
            <div className="space-y-2">
              <p className="text-xs text-gray-500">Status Breakdown</p>
              {Object.entries(deliveryMetrics?.status_breakdown || {}).slice(0, 5).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between text-sm">
                  <span className="text-gray-400 capitalize">{status.replace(/_/g, ' ')}</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product Statistics */}
        <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700 mb-4">
          <h3 className="font-bold text-white mb-3 flex items-center gap-2">
            <Package className="h-5 w-5 text-emerald-500" />
            Product Inventory Overview
            <span className="ml-auto text-xs text-gray-400">{productStats?.total_products || 0} total products</span>
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-700 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-purple-400">{productStats?.seller_products || 0}</p>
              <p className="text-xs text-gray-400">Seller Products</p>
            </div>
            <div className="bg-gray-700 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-emerald-400">{productStats?.platform_products || 0}</p>
              <p className="text-xs text-gray-400">Liantiao Products</p>
            </div>
            <div className="bg-gray-700 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-blue-400">{productStats?.products_with_variants || 0}</p>
              <p className="text-xs text-gray-400">With Variants</p>
            </div>
            <div className="bg-gray-700 rounded-xl p-3 text-center">
              <p className="text-2xl font-bold text-red-400">{productStats?.products_on_sale || 0}</p>
              <p className="text-xs text-gray-400">On Sale</p>
            </div>
          </div>
          
          {productStats?.category_breakdown?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs text-gray-400 mb-2">Top Categories</p>
              <div className="flex flex-wrap gap-2">
                {productStats.category_breakdown.slice(0, 6).map((cat, idx) => (
                  <span key={idx} className="px-2 py-1 bg-gray-600 rounded-full text-xs text-white">
                    {cat.category}: {cat.count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Merchant & Driver Performance */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Top Merchants */}
          <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
              <Store className="h-5 w-5 text-purple-500" />
              Top Merchants
              <span className="ml-auto text-xs text-gray-400">{merchantStats?.total_merchants || 0} total</span>
            </h3>
            
            <div className="space-y-2">
              {(merchantStats?.top_merchants || []).slice(0, 5).map((m, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-700 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-xs font-bold text-purple-600">
                      {idx + 1}
                    </span>
                    <span className="text-sm font-medium text-white truncate max-w-[120px]">
                      {m.business_name}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-white">{formatCurrency(m.gmv)}</p>
                    <p className="text-[10px] text-gray-400">{m.orders} orders</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Drivers */}
          <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
              <Truck className="h-5 w-5 text-blue-500" />
              Top Drivers
              <span className="ml-auto text-xs text-gray-400">{driverStats?.total_drivers || 0} total</span>
            </h3>
            
            <div className="space-y-2">
              {(driverStats?.top_drivers || []).slice(0, 5).map((d, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-700 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs font-bold text-blue-600">
                      {idx + 1}
                    </span>
                    <span className="text-sm font-medium text-white truncate max-w-[120px]">
                      {d.name}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-white">{d.total_deliveries || 0} trips</p>
                    <p className="text-[10px] text-gray-400">⭐ {(d.rating_average || 0).toFixed(1)}</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Driver Status */}
            <div className="mt-4 pt-3 border-t border-gray-700">
              <p className="text-xs text-gray-500 mb-2">Driver Status</p>
              <div className="flex gap-2">
                <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  {driverStats?.status_breakdown?.online || 0} Online
                </span>
                <span className="px-2 py-1 bg-gray-100 text-gray-400 rounded-full text-xs font-medium">
                  {driverStats?.status_breakdown?.offline || 0} Offline
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Incentives Analytics */}
        <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
          <h3 className="font-bold text-white mb-3 flex items-center gap-2">
            <Gift className="h-5 w-5 text-[#FFD300]" />
            Incentives & Promotions
          </h3>
          
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 bg-yellow-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-[#FFD300]">{incentives?.active_incentives || 0}</p>
              <p className="text-xs text-gray-500">Active Promos</p>
            </div>
            <div className="p-3 bg-gray-700 rounded-xl text-center">
              <p className="text-2xl font-bold text-white">{incentives?.total_redemptions || 0}</p>
              <p className="text-xs text-gray-500">Redemptions</p>
            </div>
            <div className="p-3 bg-green-50 rounded-xl text-center">
              <p className="text-2xl font-bold text-[#00A86B]">{formatCurrency(incentives?.total_discount_value)}</p>
              <p className="text-xs text-gray-500">Discount Value</p>
            </div>
          </div>
          
          {/* Active Incentives List */}
          {(incentives?.incentives || []).length > 0 && (
            <div className="space-y-2">
              {incentives.incentives.map((inc, idx) => (
                <div key={idx} className="flex items-center justify-between p-2 bg-gray-700 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-white">{inc.code}</p>
                    <p className="text-xs text-gray-500">{inc.description || 'Discount'}</p>
                  </div>
                  <span className="px-2 py-1 bg-[#FFD300] text-black rounded-full text-xs font-bold">
                    {inc.discount_percentage ? `${inc.discount_percentage}%` : formatCurrency(inc.discount_amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>


        {/* Search Insights - Products Not Sold */}
        {searchInsights && (
          <div className="bg-gray-800 rounded-2xl p-4 shadow-lg border border-gray-700">
            <h3 className="font-bold text-white mb-3 flex items-center gap-2">
              <Search className="h-5 w-5 text-[#FFD300]" />
              Search Insights - Missing Products
            </h3>
            <p className="text-xs text-gray-400 mb-4">Products customers are searching for but Liantiao doesn't sell</p>
            
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 bg-gray-700 rounded-xl text-center">
                <p className="text-2xl font-bold text-white">{searchInsights.total_searches?.toLocaleString() || 0}</p>
                <p className="text-xs text-gray-400">Total Searches</p>
              </div>
              <div className="p-3 bg-red-900/30 rounded-xl text-center">
                <p className="text-2xl font-bold text-red-400">{searchInsights.zero_result_searches || 0}</p>
                <p className="text-xs text-gray-400">No Results</p>
              </div>
              <div className="p-3 bg-amber-900/30 rounded-xl text-center">
                <p className="text-2xl font-bold text-amber-400">{searchInsights.zero_result_rate || 0}%</p>
                <p className="text-xs text-gray-400">Miss Rate</p>
              </div>
            </div>
            
            {searchInsights.top_missing_products?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-300">Top Missing Product Searches:</p>
                {searchInsights.top_missing_products.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-700 rounded-lg">
                    <span className="text-sm text-white">"{item.original_query || item.query}"</span>
                    <span className="px-2 py-1 bg-red-900/50 text-red-400 rounded-full text-xs font-bold">
                      {item.zero_result_count || item.search_count} searches
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Customer Support Widget */}
        <SupportWidget />
        
        {/* Storage & Analytics Widgets */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <VideoStorageWidget />
          <ShareTrackingWidget />
        </div>
        
        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
          <Button
            onClick={() => navigate('/admin/finance')}
            variant="outline"
            className="h-auto py-3 rounded-xl border-gray-700 hover:border-[#00A86B] flex flex-col items-center gap-1"
          >
            <DollarSign className="h-5 w-5 text-[#00A86B]" />
            <span className="text-xs">Finance</span>
          </Button>
          <Button
            onClick={() => navigate('/admin/routing')}
            variant="outline"
            className="h-auto py-3 rounded-xl border-gray-700 hover:border-[#00A86B] flex flex-col items-center gap-1"
          >
            <Brain className="h-5 w-5 text-[#00A86B]" />
            <span className="text-xs">Smart Routing</span>
          </Button>
          <Button
            onClick={() => navigate('/admin/trust')}
            variant="outline"
            className="h-auto py-3 rounded-xl border-gray-700 hover:border-[#00A86B] flex flex-col items-center gap-1"
          >
            <Shield className="h-5 w-5 text-[#00A86B]" />
            <span className="text-xs">Trust & Safety</span>
          </Button>
          <Button
            onClick={() => navigate('/admin/incentives')}
            variant="outline"
            className="h-auto py-3 rounded-xl border-gray-700 hover:border-[#00A86B] flex flex-col items-center gap-1"
          >
            <Award className="h-5 w-5 text-[#00A86B]" />
            <span className="text-xs">Incentives</span>
          </Button>
          <Button
            onClick={() => navigate('/admin')}
            variant="outline"
            className="h-auto py-3 rounded-xl border-gray-700 hover:border-[#00A86B] flex flex-col items-center gap-1"
          >
            <Users className="h-5 w-5 text-[#00A86B]" />
            <span className="text-xs">Users</span>
          </Button>
          <Button
            onClick={() => navigate('/driver')}
            variant="outline"
            className="h-auto py-3 rounded-xl border-gray-700 hover:border-[#00A86B] flex flex-col items-center gap-1"
          >
            <Truck className="h-5 w-5 text-[#00A86B]" />
            <span className="text-xs">Drivers</span>
          </Button>
          <Button
            onClick={() => navigate('/admin/support')}
            variant="outline"
            className="h-auto py-3 rounded-xl border-gray-700 hover:border-[#00A86B] flex flex-col items-center gap-1"
          >
            <Headphones className="h-5 w-5 text-[#00A86B]" />
            <span className="text-xs">Support</span>
          </Button>
        </div>
      </main>
    </div>
  );
};

export default CEODashboard;
